import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as apiService from '../../services/apiService';
import { Tag, TagMap, Transaction } from '../../types';
import { RootState } from '../store';
import { buildTagMap } from '../../utils/tagHelpers';

interface TagsState {
  tags: Tag[];
  debitTags: Tag[];
  creditTags: Tag[];
  tagMap: TagMap;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  debitStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  creditStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  debitError: string | null;
  creditError: string | null;
}

const initialState: TagsState = {
  tags: [],
  debitTags: [],
  creditTags: [],
  tagMap: {},
  status: 'idle',
  debitStatus: 'idle',
  creditStatus: 'idle',
  error: null,
  debitError: null,
  creditError: null,
};

export const fetchTags = createAsyncThunk<
  Tag[],
  void,
  { state: RootState; rejectValue: string }
>(
  'tags/fetchTags',
  async (_, { rejectWithValue }) => {
    try {
      return await apiService.fetchTags();
    } catch (error: any) {
      const message = error instanceof Error ? error.message : 'Failed to fetch tags';
      throw rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return state.tags.status !== 'loading';
    },
  }
);

export const fetchTagsByType = createAsyncThunk<
  { transactionType: Transaction['type']; tags: Tag[] },
  Transaction['type'],
  { state: RootState; rejectValue: string }
>(
  'tags/fetchTagsByType',
  async (transactionType, { rejectWithValue }) => {
    try {
      const tags = await apiService.fetchTags(transactionType);
      return { transactionType, tags };
    } catch (error: any) {
      const message = error instanceof Error ? error.message : 'Failed to fetch tags by transaction type';
      throw rejectWithValue(message);
    }
  },
  {
    condition: (transactionType, { getState }) => {
      const state = getState() as RootState;
      const status = transactionType === 'CREDIT' ? state.tags.creditStatus : state.tags.debitStatus;
      return status !== 'loading';
    },
  }
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTags.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTags.fulfilled, (state, action: PayloadAction<Tag[]>) => {
        state.status = 'succeeded';
        state.tags = action.payload;
        state.tagMap = buildTagMap(action.payload);
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.status = 'failed';
        state.error = typeof action.payload === 'string'
          ? action.payload
          : action.error.message ?? 'Unknown error';
      })
      .addCase(fetchTagsByType.pending, (state, action) => {
        const transactionType = action.meta.arg;
        if (transactionType === 'CREDIT') {
          state.creditStatus = 'loading';
          state.creditError = null;
        } else {
          state.debitStatus = 'loading';
          state.debitError = null;
        }
      })
      .addCase(fetchTagsByType.fulfilled, (state, action: PayloadAction<{ transactionType: Transaction['type']; tags: Tag[] }>) => {
        const { transactionType, tags } = action.payload;
        if (transactionType === 'CREDIT') {
          state.creditStatus = 'succeeded';
          state.creditTags = tags;
        } else {
          state.debitStatus = 'succeeded';
          state.debitTags = tags;
        }
      })
      .addCase(fetchTagsByType.rejected, (state, action) => {
        const transactionType = action.meta.arg;
        const errorMessage = typeof action.payload === 'string'
          ? action.payload
          : action.error.message ?? 'Unknown error';
        if (transactionType === 'CREDIT') {
          state.creditStatus = 'failed';
          state.creditError = errorMessage;
        } else {
          state.debitStatus = 'failed';
          state.debitError = errorMessage;
        }
      });
  },
});

export const selectTagMap = (state: RootState) => state.tags.tagMap;
export const selectTagsForTransactionType = (state: RootState, transactionType: Transaction['type']) =>
  transactionType === 'CREDIT' ? state.tags.creditTags : state.tags.debitTags;

export default tagsSlice.reducer;
